import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { useHashNav } from "@/utils/useHashNav";

const SERVICES = [
  { label: "CCTV & Security Cameras Installation", to: "/services/security-camera-installation/" },
  { label: "Access Control Systems", to: "/services/access-control/" },
  { label: "Intercom & Video Doorbell Installation", to: "/services/video-doorbell-intercom/" },
  { label: "Structured Cabling", to: "/services/structured-cabling/" },
  { label: "Smart Light Switches & Smart Lock Installation", to: "/services/smart-locks/" },
  { label: "Alarm & ULC Monitoring", to: "/services/alarm-ulc-monitoring/" },
  { label: "Holiday & Permanent Lighting", to: "/services/holiday-permanent-lighting/" },
  { label: "Home Theatre & Audio/Visual Systems", to: "/services/audio-visual-home-theatre/" },
  { label: "TV Mounting", to: "/services/tv-mounting/" },
  { label: "WiFi Access Points", to: "/services/wifi-access-points/" },
];

export const Footer = () => {
  const nav = useHashNav();
  return (
    <footer data-testid="site-footer" className="bg-black text-white border-t border-white/10 grain relative">
      <div className="container-x py-16 lg:py-20 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src="/assets/logo-transparent.webp" alt="SafeHome Security Solutions" width="159" height="80" loading="lazy" className="h-20 w-auto" />
          </div>
          <p className="text-sm leading-relaxed text-white/60">
            Professional security, AV, and networking installations for homes and businesses across New Brunswick.
            Authorized Verkada Dealer. Every installation backed by a 3-year warranty.
          </p>
          <div className="flex gap-3 mt-6">
            <a
              href="https://www.facebook.com/share/19GwoyA9h2/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              aria-label="SafeHome on Facebook"
              className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://www.instagram.com/safehomesecuritynb?igsh=ajN2Ymp1OHJlOWpr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="SafeHome on Instagram"
              className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.linkedin.com/in/safehome-security-solutions-inc-91a9a8428/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin-link"
              aria-label="SafeHome on LinkedIn"
              className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors"
            >
              <Linkedin className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/50 mb-5">Services</h3>
          <ul className="space-y-2.5">
            {SERVICES.map((s) => (
              <li key={s.label}>
                {s.to ? (
                  <Link to={s.to} className="text-sm text-white/75 hover:text-white transition-colors">
                    {s.label}
                  </Link>
                ) : (
                  <a
                    href={nav.href("#services")}
                    onClick={(e) => nav.go(e, "#services")}
                    className="text-sm text-white/75 hover:text-white transition-colors"
                  >
                    {s.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/50 mb-5">Company</h3>
          <ul className="space-y-2.5">
            <li><a href={nav.href("#about")} onClick={(e) => nav.go(e, "#about")} data-testid="footer-about-link" className="text-sm text-white/75 hover:text-white transition-colors">About Us</a></li>
            <li><a href={nav.href("#gallery")} onClick={(e) => nav.go(e, "#gallery")} data-testid="footer-gallery-link" className="text-sm text-white/75 hover:text-white transition-colors">Our Work</a></li>
            <li><a href={nav.href("#testimonials")} onClick={(e) => nav.go(e, "#testimonials")} data-testid="footer-reviews-link" className="text-sm text-white/75 hover:text-white transition-colors">Reviews</a></li>
            <li><a href={nav.href("#quote")} onClick={(e) => nav.go(e, "#quote")} data-testid="footer-quote-link" className="text-sm text-white/75 hover:text-white transition-colors">Get a Free Quote</a></li>
          </ul>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/50 mb-5">Contact</h3>
          <ul className="space-y-4">
            <li className="flex items-center gap-3 text-sm text-white/75">
              <Phone className="w-4 h-4 text-brand-primary shrink-0" />
              <a href="tel:+15068714144" data-testid="footer-phone-link" className="hover:text-white transition-colors">(506) 871-4144</a>
            </li>
            <li className="flex items-center gap-3 text-sm text-white/75">
              <Mail className="w-4 h-4 text-brand-primary shrink-0" />
              <a href="mailto:info@safehomesecurity.ca" data-testid="footer-email-link" className="hover:text-white transition-colors">info@safehomesecurity.ca</a>
            </li>
            <li className="flex items-start gap-3 text-sm text-white/75">
              <MapPin className="w-4 h-4 text-brand-primary shrink-0 mt-0.5" />
              Serving all of New Brunswick · Mon–Sun, 7:30am–7pm
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} SafeHome Security Solutions Inc. All rights reserved.
          </p>
          <a href="/admin" data-testid="footer-admin-link" className="text-xs text-white/40 hover:text-white/70 transition-colors">
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
};
